<template>
  <v-container id="credentials_root" fluid>
    <!-- DIALOG -->
    <router-view name="dialog"></router-view>

    <!-- CONTENT -->
    <v-row class="mb-6 px-6" align="center" justify="center">
      <v-col cols="7">
        <span class="text-h5">
          {{ $t('credentials.title.importedAlfaSignCredentials') }}
        </span>
      </v-col>

      <v-col class="d-flex flex-row align-self-end" align="sretch">
        <v-text-field
          id="search" v-model="search" :label="$t('dataTable.search')" append-icon="mdi-magnify" single-line
          hide-details clearable
        >
        </v-text-field>
      </v-col>
    </v-row>

    <v-card class="pa-5">
      <v-data-table
        id="credentials_table" must-sort :headers="_headers"
        :items="credentialList" :page="pageNum" :options.sync="pagination" :server-items-length="totalItems"
        :loading="dataTableLoading" :loading-text="$t('dataTable.content.loadingPleaseWait')"
        :item-class="() => 'credentialsRow'"
        :no-data-text="$t('dataTable.content.noDataAvailable')" :footer-props="{
          'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
          'page-text': $t('dataTable.footer.pageText'),
          'items-per-page-options': [10, 25, 50]
        }" @update:page="pageChange" @update:sort-by="sortTable" @update:sort-desc="sortTable"
      >
        <template #[`item.description`]="{ item }">
          <long-text id="description" :text="item.description" :max-char="!isAdmin ? 55 : 30" />
        </template>

        <template #[`item.user`]="{ item }">
          <long-text :text="`${item.user.firstName} ${item.user.lastName}`" />
        </template>

        <template #[`item.email`]="{ item }">
          <long-text :text="item.user.email" />
        </template>

        <template #[`item.authMode`]="{ item }">
          <span id="auth_mode">{{ getAuthModeText(item) }}</span>
        </template>

        <template #[`item.actions`]="{ item, on }">
          <!-- <div class="d-inline" v-on="on">
            <v-btn
              id="update" class="pa-1 " text
              :to="'/credentials/' + (isAdmin ? item.user.id + '/' + item.id : item.id) + '/update'"
            >
              <v-icon>
                mdi-cog-outline
              </v-icon>
              {{ $t('dataTable.content.action.update') }}
            </v-btn>
          </div>           -->

          <v-menu offset-y absolute :close-on-content-click="false">
            <template #activator="{ on: menuTrigger }">
              <v-btn id="more" text class="pa-1 " v-on="menuTrigger">
                <v-icon>mdi-dots-horizontal</v-icon>
                {{ $t('dataTable.content.action.more') }}
              </v-btn>
            </template>
            <v-list id="actions_list">
              <div class="d-inline" v-on="on">
                <v-list-item id="delete" @click="deleteCredential(item.id, $event)">
                  <v-list-item-title>{{ $t('dataTable.content.action.delete') }}</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-delete</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </div>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import debounce from 'lodash.debounce'
import { mapGetters } from 'vuex'
import Role from '../../Role'
import LongText from '../../components/LongText.vue'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../event-bus'
import alfa from '../../services/api/alfaRefs'

export default {
  name: 'CredentialsAlfaAdmin',

  components: {
    LongText
  },

  data() {
    return {
      headers: [],
      search: null,
      sortDesc: true,
      pageNum: 1,
      sortBy: ['description'],
      pagination: {
        sortBy: ['description'],
        sortDesc: [true]
      },
      credentialList: [],
      totalItems: 0,
      dataTableLoading: false,
      credentialTableActivationBtnLoading: {},
      show: false,
      selectedCredential: null,
      authorizationTimeoutMilis: this.$store.state.remoteConfig.authorizationTimeoutSecs * 1000
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },
    _headers() {
      return this.headers.filter(x => x.show)
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.searchCredentialsWatcher()
    },
    pagination: {
      handler(ev) {
        this.searchCredentials()
      }
    }
  },
  created() {
    this.headers = [
      {
        text: this.$t('dataTable.header.credentials.description'),
        align: 'left',
        value: 'description',
        show: true
      },
      {
        text: this.$t('dataTable.header.credentials.user'),
        align: 'left',
        value: 'user',
        show: this.isAdmin
      },
      {
        text: this.$t('dataTable.header.credentials.email'),
        align: 'left',
        value: 'email',
        show: this.isAdmin
      },
      {
        text: this.$t('dataTable.header.actions'),
        align: 'right',
        value: 'actions',
        sortable: false,
        show: true
      }
    ]
  },
  async mounted() {
    this.pageNum = this.$route.query.pageNum ? Number(this.$route.query.pageNum) : 1 || 1
    this.pagination.page = this.pageNum
    this.sortBy = this.$route.query.sortBy ? [this.$route.query.sortBy] : ['createdAt']
    this.pagination.sortBy = this.sortBy
    // eslint-disable-next-line no-unneeded-ternary
    this.pagination.sortDesc[0] = this.$route.query.sortOrder ? (this.$route.query.sortOrder === 'desc' ? true : false) : true

    this.searchCredentials()
  },

  methods: {
    gotoPage(page) {
      this.$nextTick(() => {
        this.pagination.page = page
      })
    },

    pageChange(newPage) {
      this.$router.replace({ name: 'CredentialsAlfaAdmin', query: { pageNum: newPage, sortBy: this.pagination.sortBy[0], sortOrder: this.pagination.sortDesc[0] ? 'desc' : 'asc' } })
    },

    sortTable() {
      this.$router.replace({ name: 'CredentialsAlfaAdmin', query: { pageNum: this.pagination.page, sortBy: this.pagination.sortBy[0], sortOrder: this.pagination.sortDesc[0] ? 'desc' : 'asc' } })
    },

    async searchCredentials() {
      this.dataTableLoading = true

      let realSortField = this.pagination.sortBy[0]
      if (realSortField === 'user') {
        realSortField = 'user.name'
      } else if (realSortField === 'email') {
        realSortField = 'user.email'
      }

      const pageRequest = {
        'page-num': this.pagination.page || 1,
        'page-size': this.pagination.itemsPerPage || 10,
        'sort-field': realSortField,
        'sort-order': this.pagination.sortDesc !== undefined ? (this.pagination.sortDesc[0] ? 'desc' : 'asc') : 'desc'
      }

      try {
        const page = await alfa.listAlfaRefs(null, this.search, pageRequest)
        this.credentialList = page.content
        this.totalItems = page.total
        this.pagination.page = page.pageNum

        // activationLoading
        this.credentialList.forEach(element => {
          this.$set(this.credentialTableActivationBtnLoading, element.id, false)
        })
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.dataTableLoading = false
      }
    },

    searchCredentialsWatcher: debounce(function() {
      this.searchCredentials()
    }, 500),

    async deleteCredential(id, e) {
      const title = this.$t('dialog.confirmation.title')
      const conf = await showConfirmationMenu({ title: title, posX: e.clientX, posY: e.clientY })
      if (!conf) {
        return
      }

      try {
        await alfa.deleteAlfaRef(id)
        this.searchCredentials()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    getAuthModeText(item) {
      return this.$t('credentials.content.importedAuthMode')
    }

  }

}
</script>
